@if (label()) {
  <label for="">{{ label() }}</label>
}
@if (description()) {
  <div class="input-description">{{ description() }}</div>
}
@let tMode = triggerMode();
@let disableTrigger = isDisabled();
@if (tMode === 'default') {
  <button
    type="button"
    class="mdw-select-trigger full-width"
    cdkOverlayOrigin
    aria-haspopup="listbox"
    [attr.aria-expanded]="isOpen()"
    [disabled]="disableTrigger"
    [class.error]="ngControl.control?.touched && errorMessages.length > 0"
    [class.has-template]="selectedItemTemplateRef() && selectedValues().length > 0"
    [class.scrollable]="selectedItemTemplateRef() && selectedValues().length > 0"
    (click)="toggle()"
  >
    @if (selectedValues().length === 0) {
      <span class="dense secondary">{{ placeholder() }}</span>
    } @else if (selectedItemTemplateRef()) {
      <div
        class="hflex-075 pad-075 flex-align-items-center flex-wrap selected-item-template scrollable fill-width"
        [style.maxHeight.px]="maxSelectedItemTemplateSize()"
      >
        @for (selectedOption of selectedValues(); track $index) {
          <mdw-chip
            [canDismiss]="!disableTrigger"
            (dismiss)="removeSelectedOption(selectedOption, $event)"
          >
            <ng-container
              *ngTemplateOutlet="
                selectedItemTemplateRef()!;
                context: { $implicit: selectedOption.rawOption }
              "
            ></ng-container>
          </mdw-chip>
        }
      </div>
    } @else {
      <span class="dense selected-value">{{ this.selectedValueDisplayField() }}</span>
    }
    @if (!selectedItemTemplateRef() || selectedValues().length === 0) {
      <mdw-icon [icon]="icons.DropdownArrow" class="chevron"></mdw-icon>
    }
  </button>
} @else {
  <button
    type="button"
    class="mdw-button mdw-muted filter-button"
    cdkOverlayOrigin
    aria-haspopup="listbox"
    [attr.aria-expanded]="isOpen()"
    [class.small-button]="filterTriggerSize() === 'small'"
    [class.has-value]="selectedValues().length > 0"
    [class.error]="ngControl.control?.touched && errorMessages.length > 0"
    [class.has-template]="selectedItemTemplateRef() && selectedValues().length > 0"
    [class.scrollable]="selectedItemTemplateRef() && selectedValues().length > 0"
    [disabled]="disableTrigger"
    (click)="toggle()"
  >
    <div class="hflex-100 flex-align-items-center">
      @if (selectedValues().length === 0) {
        <span class="small bold secondary">{{ placeholder() }}</span>
      } @else {
        <div
          class="hflex-000 flex-align-items-center filter-trigger-value-container overflow-hidden"
          [class.has-badge]="multiSelect()"
        >
          @if (multiSelect()) {
            @switch (selectedValues().length) {
              @case (1) {
                <mdw-icon [icon]="icons.BadgeNumber_1" class="large-icon"></mdw-icon>
              }
              @case (2) {
                <mdw-icon [icon]="icons.BadgeNumber_2" class="large-icon"></mdw-icon>
              }
              @case (3) {
                <mdw-icon [icon]="icons.BadgeNumber_3" class="large-icon"></mdw-icon>
              }
              @case (4) {
                <mdw-icon [icon]="icons.BadgeNumber_4" class="large-icon"></mdw-icon>
              }
              @case (5) {
                <mdw-icon [icon]="icons.BadgeNumber_5" class="large-icon"></mdw-icon>
              }
              @case (6) {
                <mdw-icon [icon]="icons.BadgeNumber_6" class="large-icon"></mdw-icon>
              }
              @case (7) {
                <mdw-icon [icon]="icons.BadgeNumber_7" class="large-icon"></mdw-icon>
              }
              @case (8) {
                <mdw-icon [icon]="icons.BadgeNumber_8" class="large-icon"></mdw-icon>
              }
              @case (9) {
                <mdw-icon [icon]="icons.BadgeNumber_9" class="large-icon"></mdw-icon>
              }
              @default {
                <mdw-icon [icon]="icons.BadgeNumber_9plus" class="large-icon"></mdw-icon>
              }
            }
          }
          <span class="small selected-value overflow-text-ellipses">
            {{ this.selectedValueDisplayField() }}
          </span>
        </div>
      }

      <mdw-icon [icon]="icons.DropdownArrow" class="chevron x-small-icon"></mdw-icon>
    </div>
  </button>
}

<ng-template
  cdkConnectedOverlay
  [cdkConnectedOverlayOrigin]="triggerOrigin()!"
  [cdkConnectedOverlayOpen]="isOpen()"
  [cdkConnectedOverlayPositions]="overlayPositions"
  (overlayOutsideClick)="onOverlayOutsideClick($event)"
  (overlayKeydown)="onOverlayKeydown($event)"
  (detach)="close()"
>
  <menu
    #menuEl
    class="surface-floating border-radius-100 select-overlay-panel"
    animate.enter="menu-enter"
    animate.leave="menu-leave"
    [style.width.px]="menuWidth() ?? undefined"
    [style.minWidth.px]="menuWidth() ? undefined : 300"
  >
    <ng-container *ngTemplateOutlet="menuContents"></ng-container>
  </menu>
</ng-template>

@if (ngControl.control?.touched && errorMessages.length > 0) {
  <div class="input-error">
    {{ errorMessages.join(', ') }}
  </div>
}

<ng-template #menuContents>
  <div class="hpad-100 pad-top-100 vflex-100 select-menu overflow-hidden">
    @if (enableSearch()) {
      <div class="select-search-container pad-bottom-050">
        <input
          #searchInput
          type="search"
          class="select-search-input full-width"
          role="combobox"
          aria-autocomplete="list"
          [placeholder]="searchPlaceholder() || 'Search...'"
          [ngModel]="search()"
          [attr.aria-expanded]="true"
          [attr.aria-controls]="instanceId + '-listbox'"
          [attr.aria-activedescendant]="activeDescendantId()"
          (ngModelChange)="search.set($event)"
          (keydown)="handleSearchKeyDown($event)"
          (clear)="search.set('')"
        />
      </div>
    }
    @if (multiSelect()) {
      <div class="hflex-000">
        <button class="mdw-button mdw-muted fill-flex" (click)="selectAll()">Select All</button>
      </div>
    }
    @let contentHeight = filteredListOptions().length * virtualItemSize() + 8;
    @let listHeight = contentHeight < menuMaxHeight() ? contentHeight : menuMaxHeight();
    <div
      role="listbox"
      [attr.id]="instanceId + '-listbox'"
      cdkVirtualScrollingElement
      [attr.aria-multiselectable]="multiSelect()"
      [style.height.px]="listHeight"
      class="scrollable list-container"
    >
      <cdk-virtual-scroll-viewport
        class="fill-height fill-width"
        minBufferPx="200"
        maxBufferPx="400"
        [itemSize]="virtualItemSize()"
      >
        <div
          mdwListItem
          role="option"
          *cdkVirtualFor="let option of filteredListOptions(); let i = index; trackBy: trackOption"
          [useCheckbox]="multiSelect()"
          [class.custom]="templateRef()"
          [class.active]="activeIndex() === i"
          [attr.id]="instanceId + '-option-' + i"
          [attr.aria-selected]="selectedValueIds().includes(option.value)"
          (click)="onOptionSelected(option.value)"
          (mouseenter)="activeIndex.set(i)"
        >
          @if (templateRef()) {
            <ng-container
              *ngTemplateOutlet="templateRef()!; context: { $implicit: option.rawOption }"
            ></ng-container>
          } @else {
            <p
              class="dense overflow-text-ellipses"
              matTooltip
              mdwTooltipIfClipped
              [tooltip]="option.label"
            >
              {{ option.label }}
            </p>
          }
        </div>
      </cdk-virtual-scroll-viewport>
    </div>
    @if (noItems()) {
      <div class="secondary pad-bottom-100">No items found</div>
    }
    @if (!enableSearch() && search().length > 0) {
      <div class="secondary pad-bottom-100">
        <small>filter: {{ search() }}</small>
      </div>
    }
  </div>
</ng-template>
